import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  ExternalDataService,
  Appointment,
  AppointmentRequirements,
  Agent,
  CreateAppointment,
} from '../../../services/externalDataService.service';
import { UpdateAppointmentFeesComponent } from '../../appointments/update-appointment-fees/update-appointment-fees.component';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-appointment-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, UpdateAppointmentFeesComponent],
  templateUrl: './appointment-detail.component.html',
  styleUrl: './appointment-detail.component.css',
})
export class AppointmentDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  appointmentId = '';
  appointment: Appointment | null = null;

  loading = false;
  errorMessage = '';

  // Remark popup
  showRemarkPopup = false;
  remarkText = '';
  actionType: 'confirm' | 'reject' | 'cancel' | null = null;
  processingAction = false;
  actionError = '';

  // Reschedule
  showRescheduleModal = false;
  rescheduleDate = '';
  rescheduleTimeSlot = '';
  rescheduleRemarks = '';
  processingReschedule = false;
  rescheduleError = '';
  timeSlotOptions: string[] = [];

  // Assign agent
  showAssignAgentModal = false;
  agents: Agent[] = [];
  selectedAgentId = '';
  loadingAgents = false;
  assigningAgent = false;
  assignAgentError = '';

  // Update fees
  showUpdateFeesModal = false;

  // Call confirmation & requirements
  availableTools: string[] = [
    'Cutting Machine',
    'Dismantling Toolkit',
    'Weighing Scale',
    'Rope & Fasteners',
    'Safety Gear',
    'Ladder',
  ];
  selectedTools: string[] = [];
  selectedVehicleType = '';
  vehicleTypeOptions: string[] = [
    'Standard (Auto / 3-Wheeler)',
    'Medium Truck (Pickup / Tata Ace)',
    'Big Vehicle (Large Truck / 14ft)',
  ];
  additionalLabourCount = 0;
  heavyLiftingRequired = false;
  specialNotes = '';
  callRemarkText = '';

  showRequirementsForm = false;
  savingRequirements = false;
  requirementsSuccessMessage = '';
  requirementsErrorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private externalDataService: ExternalDataService,
  ) {}

  ngOnInit(): void {
    this.appointmentId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.appointmentId) {
      this.errorMessage = 'Invalid appointment ID.';
      return;
    }

    this.loadGlobalSettings();
    this.loadAppointment();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAppointment(): void {
    this.loading = true;
    this.errorMessage = '';

    this.externalDataService
      .getAppointmentById(this.appointmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // API may return a single object or array
          this.appointment = Array.isArray(result) ? result[0] ?? null : (result as any);
          this.loading = false;

          // DEBUG: log raw API response
          console.log('[DEBUG] Raw API result:', JSON.stringify(result, null, 2));
          console.log('[DEBUG] appointment object:', JSON.stringify(this.appointment, null, 2));
          console.log('[DEBUG] appointment.requirements:', this.appointment?.requirements);
          console.log('[DEBUG] appointment keys:', this.appointment ? Object.keys(this.appointment) : 'null');
          if (this.appointment) {
            const raw = this.appointment as any;
            console.log('[DEBUG] raw.requirements:', raw.requirements);
            console.log('[DEBUG] raw.Requirements:', raw.Requirements);
          }

          if (this.appointment) {
            this.initRequirements();
          } else {
            this.errorMessage = 'Appointment not found.';
          }
        },
        error: (error) => {
          console.error('Error loading appointment:', error);
          this.loading = false;

          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied.';
          } else if (error.status === 404) {
            this.errorMessage = 'Appointment not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else {
            this.errorMessage = 'Failed to load appointment. Please try again.';
          }
        },
      });
  }

  loadGlobalSettings(): void {
    this.externalDataService
      .getGlobalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.timeSlotOptions =
            settings?.appointmentTimeSlots?.length
              ? settings.appointmentTimeSlots
              : ['8 AM – 1 PM', '1 PM – 5 PM'];
        },
        error: () => {
          this.timeSlotOptions = ['8 AM – 1 PM', '1 PM – 5 PM'];
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      case 'fulfilled': return 'status-fulfilled';
      case 'rescheduled': return 'status-rescheduled';
      case 'confirmed': return 'status-confirmed';
      case 'rejected': return 'status-rejected';
      case 'expired': return 'status-expired';
      default: return 'status-unknown';
    }
  }

  getGoogleMapsUrl(): string {
    if (!this.appointment?.location?.latitude || !this.appointment.location?.longitude) {
      return '';
    }
    return `https://www.google.com/maps?q=${this.appointment.location.latitude},${this.appointment.location.longitude}`;
  }

  getCurrentDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  getLatestRemark(): string {
    const history = this.appointment?.remarkHistory;
    if (history && history.length > 0) return history[history.length - 1].text;
    return this.appointment?.remarks || 'N/A';
  }

  goBack(): void {
    this.router.navigate(['/appointments-list']);
  }

  // ── Permission helpers ────────────────────────────────────────────────────

  canConfirm(): boolean { return this.appointment?.status === 'Pending'; }

  canReject(): boolean {
    const s = this.appointment?.status;
    return s === 'Pending' || s === 'Confirmed';
  }

  canCancel(): boolean {
    const s = this.appointment?.status?.toLowerCase();
    return s === 'pending' || s === 'confirmed';
  }

  canReschedule(): boolean {
    const s = this.appointment?.status?.toLowerCase();
    return s === 'pending' || s === 'rejected' || s === 'cancelled' || s === 'confirmed';
  }

  canAssignAgent(): boolean {
    const s = this.appointment?.status?.toLowerCase();
    return s !== 'expired' && s !== 'fulfilled' && s !== 'rescheduled' && s !== 'cancelled';
  }

  canUpdateFees(): boolean {
    const s = this.appointment?.status?.toLowerCase();
    return s !== 'expired' && s !== 'fulfilled' && s !== 'rescheduled' && s !== 'cancelled';
  }

  // ── Remark popup ──────────────────────────────────────────────────────────

  openRemarkPopup(type: 'confirm' | 'reject' | 'cancel'): void {
    this.actionType = type;
    this.remarkText = '';
    this.actionError = '';
    this.showRemarkPopup = true;
  }

  closeRemarkPopup(): void {
    this.showRemarkPopup = false;
    this.remarkText = '';
    this.actionType = null;
    this.actionError = '';
  }

  submitRemark(): void {
    if (!this.remarkText.trim() || !this.appointment || !this.actionType) return;

    this.processingAction = true;
    this.actionError = '';

    const id = this.appointment.id;
    const remark = this.remarkText.trim();
    let apiCall: Observable<any>;

    if (this.actionType === 'confirm') {
      apiCall = this.externalDataService.confirmAppointment(id, remark);
    } else if (this.actionType === 'reject') {
      apiCall = this.externalDataService.rejectAppointment(id, remark);
    } else {
      apiCall = this.externalDataService.cancelAppointment(id, remark);
    }

    apiCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.processingAction = false;
        this.closeRemarkPopup();
        this.loadAppointment();
      },
      error: (error) => {
        this.processingAction = false;
        this.actionError = this.resolveError(error, this.actionType!);
      },
    });
  }

  // ── Reschedule ────────────────────────────────────────────────────────────

  openRescheduleModal(): void {
    if (!this.appointment || !this.canReschedule()) return;

    this.rescheduleDate = new Date(this.appointment.date).toISOString().split('T')[0];
    this.rescheduleTimeSlot = this.appointment.timeSlot || '';
    const history = this.appointment.remarkHistory;
    this.rescheduleRemarks = history?.length ? history[history.length - 1].text : '';
    this.rescheduleError = '';
    this.showRescheduleModal = true;
  }

  closeRescheduleModal(): void {
    this.showRescheduleModal = false;
    this.rescheduleDate = '';
    this.rescheduleTimeSlot = '';
    this.rescheduleRemarks = '';
    this.rescheduleError = '';
    this.processingReschedule = false;
  }

  submitReschedule(): void {
    if (!this.appointment || !this.rescheduleDate || !this.rescheduleTimeSlot) {
      this.rescheduleError = 'Please fill in all required fields.';
      return;
    }

    this.processingReschedule = true;
    this.rescheduleError = '';

    const payload: CreateAppointment = {
      customerId: this.appointment.customerId,
      date: new Date(this.rescheduleDate).toISOString(),
      address: this.appointment.address,
      addressId: this.appointment.addressId,
      location: this.appointment.location,
      locationName: this.appointment.locationName,
      geoZoneId: this.appointment.geoZoneId,
      pinCode: this.appointment.pinCode,
      timeSlot: this.rescheduleTimeSlot,
      remarks: this.rescheduleRemarks.trim() || undefined,
      appointmentId: this.appointment.id,
    };

    this.externalDataService
      .rescheduleAppointment(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.processingReschedule = false;
          this.closeRescheduleModal();
          this.loadAppointment();
        },
        error: (error) => {
          this.processingReschedule = false;
          this.rescheduleError =
            error.error?.message || this.resolveError(error, 'reschedule');
        },
      });
  }

  // ── Assign agent ──────────────────────────────────────────────────────────

  openAssignAgentModal(): void {
    this.selectedAgentId = this.appointment?.agentId || '';
    this.assignAgentError = '';
    this.showAssignAgentModal = true;
    this.loadAgents();
  }

  closeAssignAgentModal(): void {
    this.showAssignAgentModal = false;
    this.agents = [];
    this.selectedAgentId = '';
    this.assignAgentError = '';
  }

  loadAgents(): void {
    this.loadingAgents = true;
    this.externalDataService
      .getActiveAgents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
          this.loadingAgents = false;
        },
        error: () => {
          this.assignAgentError = 'Failed to load agents. Please try again.';
          this.loadingAgents = false;
        },
      });
  }

  assignAgent(): void {
    if (!this.appointment || !this.selectedAgentId) {
      this.assignAgentError = 'Please select an agent.';
      return;
    }

    this.assigningAgent = true;
    this.assignAgentError = '';

    this.externalDataService
      .assignAgentToAppointment(this.appointment.id, this.selectedAgentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.assigningAgent = false;
          this.closeAssignAgentModal();
          this.loadAppointment();
        },
        error: (error) => {
          this.assigningAgent = false;
          this.assignAgentError = this.resolveError(error, 'assign agent');
        },
      });
  }

  openUpdateFeesModal(): void { this.showUpdateFeesModal = true; }
  onFeesUpdated(): void { this.showUpdateFeesModal = false; this.loadAppointment(); }
  onCloseFeesModal(): void { this.showUpdateFeesModal = false; }

  // ── Call confirmation & requirements ───────────────────────────────────────

  get appointmentRequirements(): AppointmentRequirements | null {
    return this.appointment?.requirements || null;
  }

  initRequirements(): void {
    if (!this.appointment) return;
    const req = this.appointmentRequirements;
    this.selectedTools = req?.tools ? [...req.tools] : [];
    this.selectedVehicleType = req?.vehicleType || '';
    this.additionalLabourCount = req?.additionalLabourCount ?? 0;
    this.heavyLiftingRequired = !!req?.heavyLiftingRequired;
    this.specialNotes = req?.specialNotes || '';

    // Always start with summary view closed by default so user sees the saved summary card
    this.showRequirementsForm = false;
  }

  toggleRequirementsForm(): void {
    this.showRequirementsForm = !this.showRequirementsForm;
    if (this.showRequirementsForm) {
      const req = this.appointmentRequirements;
      this.selectedTools = req?.tools ? [...req.tools] : [];
      this.selectedVehicleType = req?.vehicleType || '';
      this.additionalLabourCount = req?.additionalLabourCount ?? 0;
      this.heavyLiftingRequired = !!req?.heavyLiftingRequired;
      this.specialNotes = req?.specialNotes || '';
    }
  }

  getToolIconClass(tool: string): string {
    switch (tool) {
      case 'Cutting Machine':
        return 'fa-compact-disc';
      case 'Dismantling Toolkit':
        return 'fa-wrench';
      case 'Weighing Scale':
        return 'fa-scale-balanced';
      case 'Rope & Fasteners':
        return 'fa-link';
      case 'Safety Gear':
        return 'fa-user-shield';
      case 'Ladder':
        return 'fa-bars-staggered';
      default:
        return 'fa-tools';
    }
  }

  toggleTool(tool: string): void {
    const idx = this.selectedTools.indexOf(tool);
    if (idx > -1) {
      this.selectedTools.splice(idx, 1);
    } else {
      this.selectedTools.push(tool);
    }
  }

  isToolSelected(tool: string): boolean {
    return this.selectedTools.includes(tool);
  }

  getLatestCallNote(): string | null {
    if (!this.appointment?.remarkHistory?.length) return null;
    const callNotes = this.appointment.remarkHistory.filter((r) =>
      r.text?.startsWith('[Call Note]')
    );
    if (!callNotes.length) return null;
    const latest = callNotes[callNotes.length - 1];
    return latest.text.replace(/^\[Call Note\]\s*/, '');
  }

  hasSavedRequirements(): boolean {
    const req = this.appointmentRequirements;
    if (req) {
      if (
        (req.tools && req.tools.length > 0) ||
        (req.vehicleType && req.vehicleType !== '') ||
        (req.additionalLabourCount != null && req.additionalLabourCount > 0) ||
        req.heavyLiftingRequired ||
        (req.specialNotes && req.specialNotes.trim() !== '')
      ) {
        return true;
      }
    }
    return !!this.getLatestCallNote();
  }

  saveRequirements(): void {
    if (!this.appointment) return;
    this.savingRequirements = true;
    this.requirementsSuccessMessage = '';
    this.requirementsErrorMessage = '';

    const noteInput = this.callRemarkText.trim();
    const specialNotesValue =
      this.specialNotes.trim() || (noteInput ? noteInput : null);

    const payload = {
      tools: this.selectedTools,
      vehicleType: this.selectedVehicleType || null,
      additionalLabourCount: this.additionalLabourCount ?? 0,
      heavyLiftingRequired: this.heavyLiftingRequired,
      specialNotes: specialNotesValue,
      remarkText: noteInput ? `[Call Note] ${noteInput}` : null,
    };

    this.externalDataService
      .updateAppointmentRequirements(this.appointment.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedAppt) => {
          this.savingRequirements = false;
          this.requirementsSuccessMessage = 'Call confirmation requirements & notes saved!';
          this.callRemarkText = '';
          if (updatedAppt) {
            this.appointment = Array.isArray(updatedAppt) ? updatedAppt[0] ?? null : updatedAppt;
          }
          this.showRequirementsForm = false;
          this.loadAppointment();
        },
        error: (err) => {
          this.savingRequirements = false;
          this.requirementsErrorMessage = this.resolveError(err, 'save requirements');
        },
      });
  }

  // ── Error resolver ────────────────────────────────────────────────────────

  private resolveError(error: any, context: string): string {
    if (error.status === 401) return 'Authentication required. Please log in again.';
    if (error.status === 403) return 'Access denied.';
    if (error.status === 404) return 'Appointment not found.';
    if (error.status >= 500) return 'Server error. Please try again later.';
    return `Failed to ${context}. Please try again.`;
  }
}
